import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './personalInfoStep.module.scss';
import { COUNTRIES } from 'constants/countries';
import { ActionButton } from 'components/actionButton';

export interface PersonalInfoForm {
  firstName: string;
  lastName: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

interface PersonalInfoStepProps {
  values: PersonalInfoForm;
  isComplete: boolean;
  isSubmitting: boolean;
  onChange: (field: keyof PersonalInfoForm, value: string) => void;
  onSubmit: () => void;
  onGoToStep2: () => void;
}

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

/** 18 is the minimum age; 100 years back covers every plausible player. */
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 83 }, (_, i) => String(CURRENT_YEAR - 18 - i));

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  values,
  isComplete,
  isSubmitting,
  onChange,
  onSubmit,
  onGoToStep2,
}) => {
  const { t } = useTranslation();
  const isFilled = Object.values(values).every((value) => value.trim() !== '');

  return (
    <div className={styles.step}>
      {' '}
      {isComplete && <span className={styles.completeBadge}>{t('settings.verification.completed')}</span>}
      <div className={styles.stepHeader}>
        <h3 className={styles.stepTitle}>{t('settings.verification.level1Title')}</h3>
        <button
          type="button"
          className={styles.nextStepBtn}
          onClick={onGoToStep2}
          disabled={!isComplete}
        >
          {t('settings.verification.goToStep2')}
        </button>
      </div>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className={styles.gridContainer}>
          <div className={styles.inputGroup}>
            <label htmlFor="firstName">{t('settings.verification.firstName')}</label>
            <input
              id="firstName"
              className={styles.inputField}
              value={values.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="lastName">{t('settings.verification.lastName')}</label>
            <input
              id="lastName"
              className={styles.inputField}
              value={values.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.birthGrid}>
          <div className={styles.inputGroup}>
            <label htmlFor="birthDay">{t('settings.verification.birthDay')}</label>
            <select
              id="birthDay"
              className={styles.inputField}
              value={values.birthDay}
              onChange={(e) => onChange('birthDay', e.target.value)}
            >
              <option value="" />
              {DAYS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="birthMonth">{t('settings.verification.birthMonth')}</label>
            <select
              id="birthMonth"
              className={styles.inputField}
              value={values.birthMonth}
              onChange={(e) => onChange('birthMonth', e.target.value)}
            >
              <option value="" />
              {MONTH_KEYS.map((monthKey, index) => (
                <option key={monthKey} value={String(index + 1)}>
                  {t(`settings.verification.months.${monthKey}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="birthYear">{t('settings.verification.birthYear')}</label>
            <select
              id="birthYear"
              className={styles.inputField}
              value={values.birthYear}
              onChange={(e) => onChange('birthYear', e.target.value)}
            >
              <option value="" />
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="streetAddress">{t('settings.verification.streetAddress')}</label>
          <input
            id="streetAddress"
            className={styles.inputField}
            value={values.streetAddress}
            onChange={(e) => onChange('streetAddress', e.target.value)}
          />
        </div>

        <div className={styles.gridContainer}>
          <div className={styles.inputGroup}>
            <label htmlFor="city">{t('settings.verification.city')}</label>
            <input
              id="city"
              className={styles.inputField}
              value={values.city}
              onChange={(e) => onChange('city', e.target.value)}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="state">{t('settings.verification.state')}</label>
            <input
              id="state"
              className={styles.inputField}
              value={values.state}
              onChange={(e) => onChange('state', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.gridContainer}>
          <div className={styles.inputGroup}>
            <label htmlFor="country">{t('settings.verification.country')}</label>
            <select
              id="country"
              className={styles.inputField}
              value={values.country}
              onChange={(e) => onChange('country', e.target.value)}
            >
              <option value="" />
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="postalCode">{t('settings.verification.postalCode')}</label>
            <input
              id="postalCode"
              className={styles.inputField}
              value={values.postalCode}
              onChange={(e) => onChange('postalCode', e.target.value)}
            />
          </div>
        </div>

        <ActionButton
          type="submit"
          className={styles.submitBtn}
          disabled={!isFilled}
          isLoading={isSubmitting}
        >
          {isComplete ? t('settings.verification.update') : t('settings.verification.submit')}
        </ActionButton>
      </form>
    </div>
  );
};

export default PersonalInfoStep;
